import React, { useCallback, useState, createContext, useContext } from 'react';
type Lang = 'en' | 'es';
interface LanguageContextType {
  lang: Lang;
  toggleLang: () => void;
  t: (key: string) => string;
}
const translations: Record<string, Record<Lang, string>> = {
  // Navbar
  'nav.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'nav.matches': {
    en: 'Matches',
    es: 'Partidos'
  },
  'nav.deals': {
    en: 'Deals',
    es: 'Ofertas'
  },
  'nav.liveScores': {
    en: 'Live Scores',
    es: 'Resultados en Vivo'
  },
  'nav.orderYours': {
    en: 'Order Yours',
    es: 'Compra la Tuya'
  },
  // Hero
  'hero.badge': {
    en: "You're now part of Spain 2026",
    es: 'Ahora eres parte de España 2026'
  },
  'hero.title1': {
    en: "Don't Just Support Spain,",
    es: 'No Solo Apoyes a España,'
  },
  'hero.title2': {
    en: 'Live It',
    es: 'Vívelo'
  },
  'hero.subtitle': {
    en: 'Updated in real live time. Access exclusive content, match updates, and travel deals directly from your digital hub.',
    es: 'Actualizado en tiempo real. Accede a contenido exclusivo, actualizaciones de partidos y ofertas de viaje directamente desde tu hub digital.'
  },
  'hero.cta1': {
    en: 'Watch Matches',
    es: 'Ver Partidos'
  },
  'hero.cta2': {
    en: 'Explore Team',
    es: 'Explorar Equipo'
  },
  'hero.scroll': {
    en: 'Scroll to discover',
    es: 'Desliza para descubrir'
  },
  // Team Overview
  'team.label': {
    en: 'Our Legacy',
    es: 'Nuestro Legado'
  },
  'team.title1': {
    en: 'A Nation Forged',
    es: 'Una Nación Forjada'
  },
  'team.title2': {
    en: 'In Glory',
    es: 'En la Gloria'
  },
  'team.desc': {
    en: 'The Spanish national team represents more than just football; it embodies the passion, resilience, and artistry of a nation. From the historic triumph in South Africa to conquering the recent Euros, La Roja continues to write its name in the stars with their beautiful tiki-taka.',
    es: 'La selección española representa más que fútbol; encarna la pasión, resiliencia y arte de una nación. Desde el histórico triunfo en Sudáfrica hasta la conquista de la reciente Eurocopa, La Roja sigue escribiendo su nombre en las estrellas con su hermoso tiki-taka.'
  },
  'team.since': {
    en: 'Since 1920',
    es: 'Desde 1920'
  },
  'team.history': {
    en: 'Explore Our Full History →',
    es: 'Explora Nuestra Historia Completa →'
  },
  'team.euro': {
    en: 'Euro 2024',
    es: 'Euro 2024'
  },
  'team.euroDesc': {
    en: 'Champions of Europe',
    es: 'Campeones de Europa'
  },
  'team.nationsLeague': {
    en: 'Nations League',
    es: 'Nations League'
  },
  'team.nationsLeagueDesc': {
    en: '2023 Winners',
    es: 'Campeones 2023'
  },
  'team.euros': {
    en: '4 Euros',
    es: '4 Eurocopas'
  },
  'team.eurosDesc': {
    en: 'Record Championship Titles',
    es: 'Récord de Títulos Europeos'
  },
  'team.worldCups': {
    en: '1 World Cup',
    es: '1 Mundial'
  },
  'team.worldCupsDesc': {
    en: '2010 Global Champions',
    es: 'Campeones del Mundo 2010'
  },
  'team.videoLabel': {
    en: 'Sangre Española',
    es: 'Sangre Española'
  },
  'team.videoTitle1': {
    en: 'One Nation, One',
    es: 'Una Nación, Un'
  },
  'team.videoTitle2': {
    en: 'Heartbeat',
    es: 'Latido'
  },
  // Key Players
  'players.label': {
    en: 'The Squad',
    es: 'La Convocatoria'
  },
  'players.title1': {
    en: 'The Stars of',
    es: 'Las Estrellas de'
  },
  'players.title2': {
    en: 'Spain',
    es: 'España'
  },
  'players.viewSquad': {
    en: 'View Full Squad on ESPN.com',
    es: 'Ver Plantilla Completa en ESPN.com'
  },
  'players.forward': {
    en: 'Forward',
    es: 'Delantero'
  },
  'players.midfielder': {
    en: 'Midfielder',
    es: 'Centrocampista'
  },
  'players.defender': {
    en: 'Defender',
    es: 'Defensa'
  },
  'players.goalkeeper': {
    en: 'Goalkeeper',
    es: 'Portero'
  },
  // Match Center
  'matches.label': {
    en: 'World Cup 2026',
    es: 'Mundial 2026'
  },
  'matches.title1': {
    en: 'Match',
    es: 'Centro de'
  },
  'matches.title2': {
    en: 'Center',
    es: 'Partidos'
  },
  'matches.desc': {
    en: 'Follow La Roja in Group H. Get live updates, official lineups, and real-time scores from the biggest stage in football.',
    es: 'Sigue a La Roja en el Grupo H. Obtén actualizaciones en vivo, alineaciones oficiales y resultados en tiempo real del mayor escenario del fútbol.'
  },
  'matches.standings': {
    en: 'Group Standings',
    es: 'Clasificación del Grupo'
  },
  'matches.live': {
    en: 'Live',
    es: 'En Vivo'
  },
  'matches.autoRefresh': {
    en: 'Auto-refreshes every 5 min',
    es: 'Se actualiza automáticamente cada 5 min'
  },
  'matches.refreshNow': {
    en: 'Refresh Now',
    es: 'Actualizar Ahora'
  },
  'matches.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'matches.viewStandings': {
    en: 'View Live Standings on FlashScore',
    es: 'Ver Clasificación en Vivo en FlashScore'
  },
  'matches.nextMatch': {
    en: 'Next Match',
    es: 'Próximo Partido'
  },
  'matches.espn': {
    en: 'ESPN Schedule',
    es: 'Calendario ESPN'
  },
  'matches.sofascore': {
    en: 'SofaScore',
    es: 'SofaScore'
  },
  'matches.flashscore': {
    en: 'FlashScore Live',
    es: 'FlashScore en Vivo'
  },
  'matches.foxsports': {
    en: 'Fox Sports',
    es: 'Fox Sports'
  },
  // Stats
  'stats.label': {
    en: 'Spain By The Numbers',
    es: 'España en Números'
  },
  'stats.worldCup': {
    en: 'World Cup Appearances',
    es: 'Participaciones en Mundiales'
  },
  'stats.titles': {
    en: 'Major Titles',
    es: 'Títulos Principales'
  },
  'stats.goals': {
    en: 'International Goals',
    es: 'Goles Internacionales'
  },
  'stats.ramos': {
    en: 'Ramos Caps (Record)',
    es: 'Partidos de Ramos (Récord)'
  },
  // Historic Moments
  'history.label': {
    en: 'The Archives',
    es: 'Los Archivos'
  },
  'history.title1': {
    en: 'Moments That',
    es: 'Momentos Que'
  },
  'history.title2': {
    en: 'Made History',
    es: 'Hicieron Historia'
  },
  'history.wc2010': {
    en: "Iniesta's World Cup Winning Goal",
    es: 'El Gol de Iniesta que nos dio el Mundial'
  },
  'history.euro2008': {
    en: 'Torres Secures Euro 2008',
    es: 'Torres Asegura la Euro 2008'
  },
  'history.euro2012': {
    en: 'Spain 4-0 Italy (Euro 2012 Final)',
    es: 'España 4-0 Italia (Final Euro 2012)'
  },
  'history.euro2024': {
    en: 'Euro 2024 Triumph',
    es: 'Triunfo en la Euro 2024'
  },
  'history.nationsLeague': {
    en: 'Nations League 2023 Champions',
    es: 'Campeones Nations League 2023'
  },
  'history.tikitaka': {
    en: 'The Art of Tiki-Taka',
    es: 'El Arte del Tiki-Taka'
  },
  'history.yamal': {
    en: "Lamine Yamal's Stunning Goal vs France",
    es: 'El Golazo de Lamine Yamal contra Francia'
  },
  'history.casillas': {
    en: 'Iker Casillas — FIFA World Cup Saves',
    es: 'Iker Casillas — Paradas en el Mundial FIFA'
  },
  // Fan Experience
  'fan.label': {
    en: 'More Than A Game',
    es: 'Más Que Un Juego'
  },
  'fan.title1': {
    en: 'Feel the Passion of',
    es: 'Siente la Pasión de'
  },
  'fan.title2': {
    en: 'Spain',
    es: 'España'
  },
  'fan.desc': {
    en: 'Join millions of voices singing as one. Wear the colors, feel the history, and be part of the journey to global glory.',
    es: 'Únete a millones de voces cantando como una sola. Viste los colores, siente la historia y sé parte del viaje hacia la gloria mundial.'
  },
  // Tickets / Travel Deals
  'deals.label': {
    en: 'Travel to the World Cup',
    es: 'Viaja al Mundial'
  },
  'deals.title1': {
    en: 'Exclusive',
    es: 'Ofertas Exclusivas'
  },
  'deals.title2': {
    en: 'Fan Deals',
    es: 'para Fans'
  },
  'deals.desc': {
    en: 'Exclusive discounts for Spain fans on tickets, hotels, flights & transport to support La Roja in North America.',
    es: 'Descuentos exclusivos para fans de España en entradas, hoteles, vuelos y transporte para apoyar a La Roja en Norteamérica.'
  },
  'deals.unlockVip': {
    en: 'Unlock VIP Discounts',
    es: 'Desbloquea Descuentos VIP'
  },
  'deals.applyCode': {
    en: 'Apply code at checkout on partner sites',
    es: 'Aplica el código en el checkout de sitios asociados'
  },
  'deals.tickets': {
    en: 'Match Tickets',
    es: 'Entradas para Partidos'
  },
  'deals.ticketsDesc': {
    en: 'Up to 15% off group stage packages',
    es: 'Hasta 15% de descuento en paquetes de fase de grupos'
  },
  'deals.findTickets': {
    en: 'Find Tickets',
    es: 'Buscar Entradas'
  },
  'deals.hotels': {
    en: 'Hotels & Accommodation',
    es: 'Hoteles y Alojamiento'
  },
  'deals.hotelsDesc': {
    en: 'Partner hotel deals near match venues',
    es: 'Ofertas de hoteles asociados cerca de los estadios'
  },
  'deals.bookHotels': {
    en: 'Book Hotels',
    es: 'Reservar Hoteles'
  },
  'deals.flights': {
    en: 'Flights',
    es: 'Vuelos'
  },
  'deals.flightsDesc': {
    en: 'Special fares from Madrid & Barcelona to USA',
    es: 'Tarifas especiales desde Madrid y Barcelona a EE.UU.'
  },
  'deals.searchFlights': {
    en: 'Search Flights',
    es: 'Buscar Vuelos'
  },
  'deals.transport': {
    en: 'Local Transport',
    es: 'Transporte Local'
  },
  'deals.transportDesc': {
    en: 'Shuttle services & car rentals at match cities',
    es: 'Servicios de transporte y alquiler de coches en las ciudades sede'
  },
  'deals.planTransport': {
    en: 'Plan Transport',
    es: 'Planear Transporte'
  },
  // Fan HQ / Gallery
  'fanHq.label': {
    en: 'Everything You Need for Spain 2026',
    es: 'Todo lo Que Necesitas para España 2026'
  },
  'fanHq.title1': {
    en: 'Fan',
    es: 'Cuartel del'
  },
  'fanHq.title2': {
    en: 'HQ',
    es: 'Fan'
  },
  'fanHq.countdown': {
    en: "Countdown to Spain's First Match",
    es: 'Cuenta Regresiva para el Primer Partido de España'
  },
  'fanHq.days': {
    en: 'Days',
    es: 'Días'
  },
  'fanHq.hrs': {
    en: 'Hrs',
    es: 'Hrs'
  },
  'fanHq.min': {
    en: 'Min',
    es: 'Min'
  },
  'fanHq.sec': {
    en: 'Sec',
    es: 'Seg'
  },
  'fanHq.capacity': {
    en: 'Capacity',
    es: 'Capacidad'
  },
  'fanHq.chants': {
    en: 'Learn the',
    es: 'Aprende los'
  },
  'fanHq.chantsHighlight': {
    en: 'Chants',
    es: 'Cánticos'
  },
  'fanHq.nationalAnthem': {
    en: 'National Anthem',
    es: 'Himno Nacional'
  },
  'fanHq.classicChant': {
    en: 'Classic Stadium Chant',
    es: 'Cántico Clásico de Estadio'
  },
  'fanHq.crowdFavorite': {
    en: 'Crowd Favorite',
    es: 'Favorito de la Afición'
  },
  'fanHq.victoryChant': {
    en: 'Victory Chant',
    es: 'Cántico de Victoria'
  },
  'fanHq.watchYoutube': {
    en: 'Watch on YouTube',
    es: 'Ver en YouTube'
  },
  'fanHq.phrases': {
    en: 'Essential',
    es: 'Frases'
  },
  'fanHq.phrasesHighlight': {
    en: 'Phrases',
    es: 'Esenciales'
  },
  'fanHq.quickLinks': {
    en: 'Quick',
    es: 'Enlaces'
  },
  'fanHq.quickLinksHighlight': {
    en: 'Links',
    es: 'Rápidos'
  },
  'fanHq.whereToWatch': {
    en: 'Where to Watch',
    es: 'Dónde Ver'
  },
  'fanHq.whereToWatchDesc': {
    en: 'Streaming & TV channels',
    es: 'Canales de streaming y TV'
  },
  'fanHq.fanZones': {
    en: 'Fan Info Channel',
    es: 'Canal de Info para Fans'
  },
  'fanHq.fanZonesDesc': {
    en: 'Official FIFA fan information',
    es: 'Información oficial de FIFA para fans'
  },
  'fanHq.fifaRanking': {
    en: 'FIFA Ranking',
    es: 'Ranking FIFA'
  },
  'fanHq.fifaRankingDesc': {
    en: 'Official world rankings',
    es: 'Rankings mundiales oficiales'
  },
  'fanHq.fullSchedule': {
    en: 'Full WC Schedule',
    es: 'Calendario Completo del Mundial'
  },
  'fanHq.fullScheduleDesc': {
    en: 'All 104 matches',
    es: 'Todos los 104 partidos'
  },
  // Footer
  'footer.desc': {
    en: 'An official Qtap by STDI digital product. Your smart NFC T-shirt hub for Spain 2026 — real-time match data, exclusive content, and travel deals at your fingertips.',
    es: 'Un producto digital oficial de Qtap by STDI. Tu hub de camiseta NFC inteligente para España 2026 — datos de partidos en tiempo real, contenido exclusivo y ofertas de viaje al alcance de tu mano.'
  },
  'footer.resources': {
    en: 'Resources',
    es: 'Recursos'
  },
  'footer.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'footer.firstTeam': {
    en: 'First Team',
    es: 'Primer Equipo'
  },
  'footer.matches': {
    en: 'Matches',
    es: 'Partidos'
  },
  'footer.standings': {
    en: 'Standings',
    es: 'Clasificación'
  },
  'footer.travelDeals': {
    en: 'Travel Deals',
    es: 'Ofertas de Viaje'
  },
  'footer.support': {
    en: 'Support',
    es: 'Soporte'
  },
  'footer.contactUs': {
    en: 'Contact Us',
    es: 'Contáctanos'
  },
  'footer.faq': {
    en: 'FAQ',
    es: 'Preguntas Frecuentes'
  },
  'footer.privacy': {
    en: 'Privacy Policy',
    es: 'Política de Privacidad'
  },
  'footer.terms': {
    en: 'Terms of Service',
    es: 'Términos de Servicio'
  },
  'footer.cookies': {
    en: 'Cookie Policy',
    es: 'Política de Cookies'
  },
  'footer.copyright': {
    en: 'Qtap by STDI. All rights reserved.',
    es: 'Qtap by STDI. Todos los derechos reservados.'
  }
};
const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  toggleLang: () => {},
  t: (key: string) => key
});
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLang] = useState<Lang>('en');
  const toggleLang = useCallback(() => {
    setLang(prev => prev === 'en' ? 'es' : 'en');
  }, []);
  const t = useCallback((key: string) => {
    return translations[key]?.[lang] || key;
  }, [lang]);
  return <LanguageContext.Provider value={{
    lang,
    toggleLang,
    t
  }} data-id="element-211">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  return useContext(LanguageContext);
}